import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Loader2,
  Check,
  X,
} from "lucide-react";
import {
  MEMBER_CREATE_PATH,
  memberEditPath,
} from "../../routes/paths";
import {
  authGetRequest,
  authDeleteRequest,
  authPutRequest,
  getEffectiveClientId,
} from "../../api/authClient";

const formatNotification = (n: string) => {
  switch (n) {
    case "DAILY_REPORT":
      return "Daily Report";
    case "WHATSAPP_NOTIFICATION":
      return "WhatsApp";
    case "NONE":
      return "None";
    default:
      return n;
  }
};

const getNotificationLabels = (client: any): string[] => {
  const frequencySource = client?.report_frequency ?? client?.reportFrequency;
  const fromFrequency = Array.isArray(frequencySource)
    ? frequencySource.map((n: string) => formatNotification(String(n)))
    : [];

  const fromFlags: string[] = [];
  if (client?.isEmailEnable === true || client?.is_email_enable === true) fromFlags.push("Email");
  if (client?.isWhatsappEnable === true || client?.is_whatsapp_enable === true) fromFlags.push("WhatsApp");

  const merged = [...fromFrequency, ...fromFlags].filter(Boolean);
  return Array.from(new Set(merged));
};

const getStatusMeta = (
  statusRaw: unknown
): { label: string; className: string } => {
  const status = String(statusRaw ?? "").toUpperCase();
  if (status === "ACTIVE") {
    return {
      label: "Approved",
      className:
        "inline-flex px-2 py-1 text-xs font-medium rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-500/25",
    };
  }
  if (status === "VERIFIED") {
    return {
      label: "Verified",
      className:
        "inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-500/10 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-500/25",
    };
  }
  if (status === "INACTIVE") {
    return {
      label: "Pending",
      className:
        "inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-500/10 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/25",
    };
  }
  return {
    label: "Pending",
    className:
      "inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/25",
  };
};

export default function Member() {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(clients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = clients.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [clients.length]);

  useEffect(() => {
    const fetchClients = async () => {
      const clientId = getEffectiveClientId();
      if (!clientId) {
        setClients([]);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const payload: any = await authGetRequest(
          `/client/viewers/${clientId}`
          // { role: "ADMIN" }
        );

        const data = payload?.data ?? payload?.response ?? payload;
        const list: any[] =
          (Array.isArray(data) && data) ||
          (Array.isArray(data?.items) && data.items) ||
          (Array.isArray(data?.results) && data.results) ||
          (Array.isArray(data?.data) && data.data) ||
          [];

        setClients(list);
      } catch (err: any) {
        setError(err?.response?.data?.message ?? "Failed to load members.");
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowActionsDropdown(false);
        setSelectedUser(null);
      }
    };
    if (showActionsDropdown) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showActionsDropdown]);

  const openActionsDropdown = (user: any, event: React.MouseEvent) => {
    const button = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setDropdownPosition({ top: button.bottom + 5, left: button.right - 200 });
    setSelectedUser(user);
    setShowActionsDropdown(true);
  };

  const confirmDelete = (id: string, name: string) => {
    setUserToDelete({ id, name });
    setShowDeleteConfirm(true);
    setShowActionsDropdown(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await authDeleteRequest(`/client/${userToDelete.id}`);
      setClients((prev) => prev.filter((c: any) => c.id !== userToDelete.id));
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to delete member.");
    } finally {
      setIsDeleting(false);
    }
  };

  const updateMemberStatus = async (member: any, status: "ACTIVE" | "INACTIVE") => {
    setIsUpdatingStatus(true);
    setError(null);
    try {
      await authPutRequest(`/client/${member.id}`, { status });
      setClients((prev) =>
        prev.map((c: any) => (c.id === member.id ? { ...c, status } : c))
      );
      setShowActionsDropdown(false);
      setSelectedUser(null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to update member status.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const thClass =
    "px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider dark:text-slate-400";
  const tdClass = "px-6 py-5 text-sm text-slate-700 dark:text-slate-300";

  return (
    <div className="p-5 min-h-full overflow-y-auto font-sans transition-colors duration-300 relative bg-white text-slate-900 dark:bg-transparent dark:text-slate-200 space-y-5">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#1C202F] rounded-xl border border-slate-200 dark:border-slate-700/80 max-w-md w-full overflow-hidden shadow-2xl">
            <div className="p-6">
              <div className="w-12 h-12 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20">
                <Trash2 className="h-6 w-6 text-red-500 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 text-center">Delete Member</h3>
              <p className="mt-3 text-center text-slate-600 dark:text-slate-400">
                Are you sure you want to delete <span className="font-semibold text-slate-800 dark:text-white">{userToDelete?.name}</span>?
                This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700 p-4">
              <button
                type="button"
                onClick={cancelDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-[#1C202F] border border-slate-300 dark:border-[#334155] rounded-lg hover:bg-slate-100 dark:hover:bg-[#1C202F]/70 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete Member
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions Dropdown */}
      {showActionsDropdown && selectedUser && (
        <div
          ref={dropdownRef}
          className="fixed z-50 w-48 bg-white dark:bg-[#1C202F] rounded-lg border border-slate-200 dark:border-slate-700/70 shadow-xl overflow-hidden backdrop-blur-sm"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <div className="py-1">
            {selectedUser.status === "ACTIVE" && (
              <button
                onClick={() => {
                  navigate(memberEditPath(selectedUser.id));
                  setShowActionsDropdown(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/40 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                <Pencil className="h-4 w-4" />
                Edit Member
              </button>
            )}
            <button
              disabled={isUpdatingStatus || selectedUser.status !== "VERIFIED"}
              onClick={() => updateMemberStatus(selectedUser, "ACTIVE")}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 dark:hover:bg-emerald-500/20 hover:text-emerald-800 dark:hover:text-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Approve Member
            </button>
            <button
              disabled={isUpdatingStatus || selectedUser.status === "INACTIVE"}
              onClick={() => updateMemberStatus(selectedUser, "INACTIVE")}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 dark:hover:bg-amber-500/20 hover:text-amber-800 dark:hover:text-amber-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              Reject Member
            </button>
            <button
              onClick={() => confirmDelete(selectedUser.id, selectedUser.full_name || "this user")}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-red-500/10 dark:hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-300 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete Member
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-slate-600 dark:text-slate-400">
            Manage members accounts and notification preferences
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-blue-500/10 dark:bg-blue-500/20 px-4 py-2 rounded-lg border border-blue-500/20 dark:border-blue-500/30">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
              {clients.length} Member{clients.length !== 1 ? "s" : ""}
            </span>
          </div>
          <button
            onClick={() => navigate(MEMBER_CREATE_PATH)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Member
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/80 dark:bg-[#1C202F]/55 rounded-xl border border-slate-200 dark:border-slate-700/70 overflow-hidden backdrop-blur-sm">
        {error && (
          <div className="px-6 py-3 text-sm text-red-600 dark:text-red-300 border-b border-slate-200 dark:border-slate-700 bg-red-500/10">
            {error}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100/70 dark:bg-[#1C202F]/45 border-b border-slate-200 dark:border-slate-700/80">
              <tr>
                <th className={thClass}>S.No.</th>
                <th className={thClass}>Member</th>
                <th className={thClass}>Contact</th>
                {/* <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Role</th> */}
                <th className={thClass}>Notifications</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700/50">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-slate-500 dark:text-slate-400">
                    Loading members...
                  </td>
                </tr>
              ) : paginatedClients.length > 0 ? (
                paginatedClients.map((client: any, index: number) => (
                  <tr key={client?.id} className="hover:bg-slate-100 dark:hover:bg-[#1C202F]/55 transition-colors">
                    <td className={`${tdClass} font-medium text-slate-900 dark:text-white`}>{startIndex + index + 1}</td>
                    <td className={tdClass}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500/10 dark:bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-500/15 dark:border-blue-500/25">
                          <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900 dark:text-white">{client?.full_name || "Unnamed"}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{client?.email || "No email"}</div>
                        </div>
                      </div>
                    </td>
                    <td className={tdClass}>{client?.contact || "—"}</td>
                    {/* <td className="px-6 py-5">
                      <div>
                        <div className="text-sm text-white">{client?.designation || "—"}</div>
                        <div className="text-xs text-slate-400">{client?.department || "—"}</div>
                      </div>
                    </td> */}
                    <td className={tdClass}>
                      <div className="flex flex-wrap gap-1">
                        {getNotificationLabels(client).length > 0 ? (
                          getNotificationLabels(client).map((n: string, i: number) => (
                            <span key={i} className="px-2 py-1 text-xs font-medium rounded-full bg-blue-500/10 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-500/25">
                              {n}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-500">None</span>
                        )}
                      </div>
                    </td>
                    <td className={tdClass}>
                      {(() => {
                        const statusMeta = getStatusMeta(client?.status);
                        return <span className={statusMeta.className}>{statusMeta.label}</span>;
                      })()}
                    </td>
                    <td className={tdClass}>
                      <button
                        onClick={(e) => openActionsDropdown(client, e)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white dark:bg-[#1C202F] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#1C202F]/70 hover:text-slate-900 dark:hover:text-white border border-slate-300 dark:border-[#334155] transition-colors"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700/50 rounded-full flex items-center justify-center border border-slate-200 dark:border-slate-700/70">
                        <User className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">No members found</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Get started by creating your first member account</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {clients.length > itemsPerPage && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700/70 flex items-center justify-between bg-slate-100/60 dark:bg-[#1C202F]/25">
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Showing {startIndex + 1} to {Math.min(endIndex, clients.length)} of {clients.length} results
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-[#1C202F] border border-slate-300 dark:border-[#334155] rounded-lg hover:bg-slate-100 dark:hover:bg-[#1C202F]/70 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = totalPages <= 5 ? i + 1 : currentPage <= 3 ? i + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                      currentPage === page
                        ? "bg-blue-600 text-white"
                        : "bg-white dark:bg-[#1C202F] text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-[#334155] hover:bg-slate-100 dark:hover:bg-[#1C202F]/70"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-[#1C202F] border border-slate-300 dark:border-[#334155] rounded-lg hover:bg-slate-100 dark:hover:bg-[#1C202F]/70 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
